import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LOOKUP_PAGE_SIZE,
} from '../constants';
import type {
  FacilityRecord,
  FacilityTotalsParams,
  PagedResult,
} from '../types/index';

function normalizeFacilities(facilities: FacilityRecord[] = []): FacilityRecord[] {
  return (facilities || [])
    .filter((facility) => facility?.active !== false)
    .map((facility) => ({
      ...facility,
      id: facility?.id,
      name: facility?.name || '',
      license: facility?.license || '',
    }));
}

export async function getFacilities(
  organizationId: number | string,
  marketId?: number | string | null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE
): Promise<FacilityRecord[]> {
  const response = await axios.get('/facility', {
    params: { organizationId, marketId, page, pageSize },
  });
  return normalizeFacilities(Array.isArray(response.data) ? response.data : []);
}

export async function getFacilitiesTotals({
  organizationId,
  name = null,
  marketId = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
  sortField = null,
  sortAscending = null,
}: FacilityTotalsParams = {}): Promise<PagedResult<FacilityRecord>> {
  const response = await axios.get('/facility/totals', {
    params: { organizationId, name, marketId, page, pageSize, sortField, sortAscending },
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: normalizeFacilities(Array.isArray(response.data?.data) ? response.data.data : []),
  };
}

export async function getFacilitiesByOrganization(
  organizationId: number | string,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE
): Promise<PagedResult<FacilityRecord>> {
  const response = await axios.get('/facility/organization', {
    params: { organizationId, page, pageSize },
  });

  const responseData = response.data;
  const facilities = Array.isArray(responseData?.data)
    ? responseData.data
    : (Array.isArray(responseData) ? responseData : []);

  return {
    page: Number(responseData?.page ?? page),
    pageSize: Number(responseData?.pageSize ?? pageSize),
    total: Number(responseData?.total ?? facilities.length ?? 0),
    data: normalizeFacilities(facilities),
  };
}

export async function searchFacilitiesByLicense(
  license: string,
  page = DEFAULT_LIST_PAGE,
  pageSize = 50,
): Promise<PagedResult<FacilityRecord>> {
  const response = await axios.get('/facility/license-search', {
    params: { license, page, pageSize },
  });
  const data = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? data.length),
    data,
  };
}

export async function addFacility(payload: unknown): Promise<unknown> {
  const response = await axios.post('/facility/add', payload);
  return response.data;
}

export async function editFacility(payload: unknown): Promise<unknown> {
  const response = await axios.post('/facility/edit', payload);
  return response.data;
}
